import { format } from 'date-fns'
import * as React from 'react'
import type { DayButtonProps, DayProps } from 'react-day-picker'
import { DayButton } from 'react-day-picker'

import { toUTCNoon } from '@/lib/date/date'
import { cn } from '@/lib/ui/style-helpers'

import { Calendar } from '@/components/lemmala-design-system/calendar/calendar'

import { CalendarHeader } from './calendar-header'
import { DatePickerCalendarProps } from './types'
import {
  dayCellVariants,
  getNextMonth,
  getNextYear,
  getPreviousMonth,
  getPreviousYear,
  getRangeDayState,
} from './utils'

type DatePickerCalendarContextValue = {
  showSelectedState: boolean
  variant: DatePickerCalendarProps['variant']
  rangeStart?: Date
  rangeEnd?: Date
  hoverDate?: Date
}

const DatePickerCalendarContext = React.createContext<DatePickerCalendarContextValue | null>(null)

const useDatePickerCalendarContext = () => {
  const context = React.useContext(DatePickerCalendarContext)

  if (!context) {
    throw new Error('DatePickerCalendarContext provider is missing')
  }

  return context
}

/**
 * Custom day button component with gridcell role and selected state styling
 */
type DayButtonWithGridcellRoleProps = DayButtonProps & {
  showSelectedState?: boolean
  variant?: DatePickerCalendarProps['variant']
  rangeStart?: Date
  rangeEnd?: Date
  hoverDate?: Date
}

const DayButtonWithGridcellRole = (props: DayButtonWithGridcellRoleProps) => {
  const {
    day,
    modifiers,
    className,
    showSelectedState = true,
    variant = 'default',
    rangeStart,
    rangeEnd,
    hoverDate,
    ...rest
  } = props
  const rangeDayState =
    variant === 'range' && !modifiers.outside
      ? getRangeDayState({
          date: day.date,
          rangeStart,
          rangeEnd,
          hoverDate,
        })
      : 'default'
  const hasSelectedState =
    showSelectedState && modifiers.selected && !modifiers.rangeStart && !modifiers.rangeEnd
  const hasDefaultHover = !modifiers.disabled && !modifiers.outside && rangeDayState === 'default'

  return (
    <DayButton
      role='gridcell'
      className={cn(className, {
        'hover:!bg-main-background-interactive-list-hover hover:!text-main-foreground-primary':
          hasDefaultHover,
        'rounded-sm !bg-main-background-subtle-semantic-accent !text-main-foreground-accent':
          hasSelectedState,
        'hover:!bg-main-background-subtle-semantic-accent hover:!text-main-foreground-accent':
          hasSelectedState,
        '!bg-main-background-subtle-semantic-accent/60 hover:!bg-main-background-subtle-semantic-accent/60 !text-main-foreground-accent hover:!text-main-foreground-accent':
          rangeDayState === 'previewRangeStart' || rangeDayState === 'previewRangeEnd',
      })}
      modifiers={modifiers}
      day={day}
      {...rest}
    />
  )
}

const DayButtonWithGridcellRoleFromContext = (props: DayButtonProps) => {
  const { showSelectedState, variant, rangeStart, rangeEnd, hoverDate } =
    useDatePickerCalendarContext()

  return (
    <DayButtonWithGridcellRole
      {...props}
      showSelectedState={showSelectedState}
      variant={variant}
      rangeStart={rangeStart}
      rangeEnd={rangeEnd}
      hoverDate={hoverDate}
    />
  )
}

/**
 * Custom day cell component without role attribute
 */
type DayCellWithoutRoleProps = DayProps & {
  variant?: DatePickerCalendarProps['variant']
  rangeStart?: Date
  rangeEnd?: Date
  hoverDate?: Date
}

const DayCellWithoutRole: React.FC<DayCellWithoutRoleProps> = ({
  role: _role,
  className,
  children,
  day,
  variant = 'default',
  rangeStart,
  rangeEnd,
  hoverDate,
  ...rest
}) => {
  const rangeDayState =
    variant === 'range' && !day.outside
      ? getRangeDayState({
          date: day.date,
          rangeStart,
          rangeEnd,
          hoverDate,
        })
      : 'default'

  return (
    <td className={cn(className, dayCellVariants({ state: rangeDayState }))} {...rest}>
      {children}
    </td>
  )
}

const DayCellWithoutRoleFromContext: React.FC<DayProps> = (props) => {
  const { variant, rangeStart, rangeEnd, hoverDate } = useDatePickerCalendarContext()

  return (
    <DayCellWithoutRole
      {...props}
      variant={variant}
      rangeStart={rangeStart}
      rangeEnd={rangeEnd}
      hoverDate={hoverDate}
    />
  )
}

/**
 * Hidden caption component for accessibility
 */
const HiddenCaption = () => <span aria-hidden className='hidden' />

export const DatePickerCalendar: React.FC<DatePickerCalendarProps> = ({
  value,
  onChange,
  disabledDates,
  month,
  onMonthChange,
  containerClassName,
  showPrevNav = true,
  showNextNav = true,
  showSelectedState = true,
  variant = 'default',
  rangeStart,
  rangeEnd,
  hoverDate,
  onDayMouseEnter,
  onDayMouseLeave,
}) => {
  const [internalMonth, setInternalMonth] = React.useState(value || new Date())
  const displayedMonth = month || internalMonth
  const handleMonthChange = onMonthChange || setInternalMonth
  const contextValue = React.useMemo(
    () => ({
      showSelectedState,
      variant,
      rangeStart,
      rangeEnd,
      hoverDate,
    }),
    [showSelectedState, variant, rangeStart, rangeEnd, hoverDate],
  )
  const components = React.useMemo(
    () => ({
      MonthCaption: HiddenCaption,
      CaptionLabel: HiddenCaption,
      DayButton: DayButtonWithGridcellRoleFromContext,
      Day: DayCellWithoutRoleFromContext,
    }),
    [],
  )

  const handlePrevMonth = () => {
    handleMonthChange(getPreviousMonth(displayedMonth))
  }

  const handleNextMonth = () => {
    handleMonthChange(getNextMonth(displayedMonth))
  }

  const handlePrevYear = () => {
    handleMonthChange(getPreviousYear(displayedMonth))
  }

  const handleNextYear = () => {
    handleMonthChange(getNextYear(displayedMonth))
  }

  const handleYearChange = (year: number) => {
    const nextMonth = new Date(displayedMonth)
    nextMonth.setDate(1)
    nextMonth.setFullYear(year)
    handleMonthChange(nextMonth)
  }

  return (
    <DatePickerCalendarContext.Provider value={contextValue}>
      <div
        className={cn(
          'overflow-hidden rounded border border-main-border-hint p-0',
          containerClassName,
        )}
        style={{ boxSizing: 'border-box' }}
      >
        <CalendarHeader
          month={displayedMonth}
          onPreviousYear={handlePrevYear}
          onPreviousMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
          onNextYear={handleNextYear}
          onYearChange={handleYearChange}
          showPrevNav={showPrevNav}
          showNextNav={showNextNav}
        />
        <div className='h-1 w-full bg-main-background-card' />
        <Calendar
          mode='single'
          selected={value}
          onSelect={(date) => {
            const next = date ?? value
            if (next) onChange?.(toUTCNoon(next))
          }}
          month={displayedMonth}
          onMonthChange={handleMonthChange}
          disabled={disabledDates || ((date) => date.getMonth() !== displayedMonth.getMonth())}
          hideNavigation
          className='mx-auto w-fit'
          onDayMouseEnter={onDayMouseEnter}
          onDayMouseLeave={onDayMouseLeave}
          components={components as React.ComponentProps<typeof Calendar>['components']}
          formatters={{
            formatWeekdayName: (date) => format(date, 'EEE'),
          }}
        />
      </div>
    </DatePickerCalendarContext.Provider>
  )
}
